'use client';

import React from 'react';
import SlideWrapper from '../SlideWrapper';
import { Leaf, Utensils, Fish, Camera, Video } from 'lucide-react';

interface Props {
  id: string;
  registerRef: (id: string, el: HTMLElement | null) => void;
}

const activities = [
{
  icon: <Leaf size={18} className="text-[#4caf7d]" />,
  time: '08:00 – 09:30',
  title: 'Sinh hoạt – nhận bộ kit đạo cụ',
  desc: 'Sinh viên check-in, nhận khăn bandana hoặc passport chương trình, chia đội. Định vị vai trò từng thành viên.',
  accent: '#4caf7d'
},
{
  icon: <Leaf size={18} className="text-[#a8d87a]" />,
  time: '09:30 – 12:00',
  title: 'Green Volunteer – Gom rác, phân loại, ghi hình before–after',
  desc: 'Các đội chia khu vực thực hiện gom rác, phân loại và ghi lại hiện trạng trước – sau.',
  accent: '#a8d87a'
},
{
  icon: <Utensils size={18} className="text-[#e8a030]" />,
  time: '12:00 – 14:00',
  title: 'First Local Taste – Ăn trưa, thưởng thức đặc sản',
  desc: 'Trải nghiệm bữa ăn địa phương, kết nối với thiên nhiên và văn hoá bản địa.',
  accent: '#e8a030'
},
{
  icon: <Fish size={18} className="text-[#4caf7d]" />,
  time: '14:00 – 17:30',
  title: 'Khởi động khu dòng nước – thả cá (Đào mương cá 1)',
  desc: 'Sinh viên chia nhóm thực hiện: dọn nền, xác định lối đi, định hình khu quan sát và vị trí thả cá.',
  accent: '#4caf7d'
},
{
  icon: <Camera size={18} className="text-[#f5c96a]" />,
  time: '17:30 – 18:00',
  title: 'Ghi hình thành quả đầu ngày',
  desc: 'Chụp ảnh đội nhóm, quay flycam, ghi lại before–after.',
  accent: '#f5c96a'
},
{
  icon: <Utensils size={18} className="text-[#e8a030]" />,
  time: '18:30 – 20:00',
  title: 'Nướng BBQ – ăn tối',
  desc: 'Các đội cùng chuẩn bị, chế biến và thưởng thức bữa tối.',
  accent: '#e8a030'
},
{
  icon: <Video size={18} className="text-[#a8d87a]" />,
  time: '20:00 – 21:30',
  title: 'Lửa trại & video challenge',
  desc: 'Tổ chức lửa trại, mỗi đội quay video ngắn recap ngày đầu, ghi lại cảm nhận và khoảnh khắc đáng nhớ.',
  accent: '#a8d87a'
}];


export default function Slide08Day1({ id, registerRef }: Props) {
  return (
    <SlideWrapper id={id} registerRef={registerRef} className="bg-[#0a1a0f]">
      <div className="absolute inset-0">
        <img
          src="https://img.rocket.new/generatedImages/rocket_gen_img_13a59b9f9-1775560504594.png"
          alt="Tình nguyện viên sinh viên tập hợp tại khu vực thiên nhiên xanh, chuẩn bị cho hoạt động dọn dẹp và bảo vệ môi trường"
          className="w-full h-full object-cover opacity-8" />

        <div className="absolute inset-0 bg-gradient-to-br from-[#0a1a0f] via-[#1a3a2a]/40 to-[#0a1a0f]" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[rgba(76,175,125,0.3)] to-transparent" />
      </div>

      <div className="relative z-10 max-w-screen-xl mx-auto px-6 lg:px-16 xl:px-24 py-24 pt-28 min-h-screen flex flex-col justify-center">
        <div className="fade-in-up mb-4">
          <div className="chapter-badge">
            <span className="w-1.5 h-1.5 rounded-full bg-[#4caf7d] inline-block" />
            Hành trình 8 ngày 7 đêm
          </div>
        </div>

        <div className="fade-in-up mb-3">
          <h2 className="font-display font-800 text-[clamp(24px,3.5vw,48px)] leading-[1.1] text-[#f0ede6] tracking-tight">
            NGÀY 1
          </h2>
          <h2 className="font-display font-800 text-[clamp(18px,2.5vw,32px)] leading-[1.3] tracking-tight text-gradient-green">Khởi động – Làm sạch – Lửa trại

          </h2>
        </div>

        <div className="fade-in-up mb-8">
          <div className="section-divider" />
        </div>

        <div className="relative fade-in-up">
          <div className="absolute left-[22px] top-0 bottom-0 w-px bg-gradient-to-b from-[#4caf7d]/60 via-[#e8a030]/40 to-transparent" />
          <div className="flex flex-col gap-4">
            {activities.map((act, i) =>
            <div key={`d1-${i}`} className="flex items-start gap-4 pl-2">
                <div
                className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center z-10"
                style={{ background: `${act.accent}22`, border: `1.5px solid ${act.accent}55` }}>

                  {act.icon}
                </div>
                <div
                className="flex-1 rounded-xl p-4 transition-all duration-300 hover:-translate-y-0.5"
                style={{ background: `${act.accent}0d`, border: `1px solid ${act.accent}22` }}>

                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <span
                    className="font-body font-700 text-[11px] tabular-nums px-2 py-0.5 rounded-full"
                    style={{ color: act.accent, background: `${act.accent}18` }}>

                      {act.time}
                    </span>
                    <h3 className="font-display font-700 text-[14px] lg:text-[15px] text-[#f0ede6]">
                      {act.title}
                    </h3>
                  </div>
                  <p className="font-body text-[12px] lg:text-[13px] text-[#a0b8a8] leading-relaxed">
                    {act.desc}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </SlideWrapper>);

}